import { EditOutlined, LeftOutlined } from '@ant-design/icons'
import { Button, Card, Flex, Spin } from 'antd'
import { useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'

import { useTreatmentServiceGetConsultationEndpoint } from 'src/api/queries'
import { ConsultationType, Permissions } from 'src/api/requests'
import { PageBreadcrumbs } from 'src/components'
import {
  ConsultationFormDetails,
  DetailsFinalCard,
  DetailsGeneralInformationCard,
  DetailsLaboratoryCard,
  DetailsPregnancy,
  DetailsPreliminaryCard,
  DetailsTbScreeningCard,
  TreatmentDetails
} from 'src/features/persons/components'
import {
  ContactsForm,
  ContactsLayout,
  ContactsSection
} from 'src/features/persons/components/PersonalDetails/components/ContactsForm'
import { usePersonBreadcrumb } from 'src/features/persons/hooks'
import { useUser } from 'src/hooks'
import { useLocales } from 'src/locales'
import { paths } from 'src/paths'

import { useStyles } from './styles'

export const ConsultationDetailsPage = () => {
  const { t } = useLocales()
  const { styles } = useStyles()

  const { personId, id } = useParams()
  const navigate = useNavigate()
  const { pathname } = useLocation()

  const { hasPermission } = useUser()
  const [isContactsOpen, setIsContactsOpen] = useState(false)

  const onToggle = () => setIsContactsOpen((prev) => !prev)

  const { data: consultation, isLoading: isLoadingConsultation } =
    useTreatmentServiceGetConsultationEndpoint({ id: String(id) }, [id], {
      enabled: !!id
    })

  const { breadcrumbItems, fullName, isLoadingPerson } =
    usePersonBreadcrumb(personId)

  const onBackClick = () => {
    navigate(-1)
  }

  // A performed consultation can be edited only by its author, on the day it was performed.
  const onEditClick = () => {
    if (!personId || !id) return

    navigate(
      pathname.includes('patients')
        ? paths.patients.consultations.edit(personId, id)
        : paths.persons.consultations.edit(personId, id)
    )
  }

  const isPrimary =
    consultation?.generalInformation?.consultationType ===
    ConsultationType.PRIMARY

  return (
    <Spin spinning={isLoadingPerson || isLoadingConsultation}>
      <PageBreadcrumbs title={fullName} items={breadcrumbItems}>
        <Flex vertical gap={24}>
          <Flex justify="space-between" gap={16}>
            <Button
              icon={<LeftOutlined />}
              onClick={onBackClick}
              className={styles.button}
            >
              {t('common.back')}
            </Button>

            {consultation?.canEdit && (
              <Button
                type="primary"
                icon={<EditOutlined />}
                onClick={onEditClick}
              >
                {t('common.edit')}
              </Button>
            )}
          </Flex>

          <DetailsGeneralInformationCard data={consultation} />

          {consultation?.examinationId && (
            <ConsultationFormDetails isPrimary={isPrimary} />
          )}

          {consultation?.tbScreening && (
            <DetailsTbScreeningCard screening={consultation.tbScreening} />
          )}

          {consultation?.preliminaryInformation && (
            <DetailsPreliminaryCard data={consultation} />
          )}

          {consultation?.pregnancyMonitoringId && (
            <DetailsPregnancy data={consultation} />
          )}

          <DetailsLaboratoryCard
            investigationDemandsIds={consultation?.investigationDemandsIds}
          />

          {consultation?.assessment && <DetailsFinalCard data={consultation} />}

          {consultation?.treatmentPlanId && (
            <TreatmentDetails data={consultation} />
          )}

          {hasPermission(
            Permissions.TREATMENT_CONSULTATION_VIEW_POTENTIAL_CONTACTS_INSTITUTION
          ) && (
            <Card>
              <ContactsLayout onToggle={onToggle}>
                <ContactsForm />
              </ContactsLayout>
            </Card>
          )}

          {isContactsOpen && <ContactsSection onCancel={onToggle} />}
        </Flex>
      </PageBreadcrumbs>
    </Spin>
  )
}
